// The HUMAN a fleet commit credits — the `Co-authored-by:` trailer GitHub reads.
//
// WHY. Every commit the fleet writes is authored by `developerz-ai[bot]`
// (`apps/runner/src/modes/worker/team/git/commit.ts`), so the person who asked for the
// work — who filed the ask, started the plan, dispatched the task — gets nothing on
// GitHub for it: no avatar on the commit, no square on the contribution graph. GitHub
// credits a co-author from a `Co-authored-by: <name> <email>` trailer whose email is
// one of that account's addresses, and `<numeric id>+<login>@users.noreply.github.com`
// is the address GitHub itself issues to every account: it attributes without the
// person having published an email, and it survives a login rename because the id
// half is what GitHub keys on.
//
// WHO. Resolved mothership-side at claim (`findTaskCoAuthor`, `@developerz/db`) — a
// box cannot read `users` — and shipped on the assignment as `co_author`, only for a
// lane in `ORIGIN_READS_CO_AUTHOR`. Absent ⇒ no trailer, never a failure: a system
// lane, an unbound token and a user with no linked GitHub identity all credit nobody.
//
// The bot is never a co-author ({@link FleetCoAuthorSchema} refuses a `[bot]` login by
// its login regex), and the trailer never names the commit's own author twice
// ({@link appendCoAuthorTrailer}).

import { z } from 'zod';

/** The trailer key, spelled the way GitHub's own UI writes it. Matched case-insensitively. */
export const CO_AUTHOR_TRAILER_KEY = 'Co-authored-by' as const;

/**
 * A GitHub user login: 1–39 alphanumerics or single hyphens, not starting with a
 * hyphen. Deliberately refuses `[bot]` — an App is never credited as a person.
 */
const GITHUB_LOGIN = /^[A-Za-z0-9](?:[A-Za-z0-9]|-(?=[A-Za-z0-9])){0,38}$/;

/**
 * `co_author` on the assignment: the GitHub identity of the human behind the task.
 * `github_user_id` is `users.github_user_id` (stable across renames); `login` is the
 * handle as last seen, used for the display name and the no-reply local part.
 */
export const FleetCoAuthorSchema = z.object({
  github_user_id: z.number().int().positive(),
  login: z.string().regex(GITHUB_LOGIN),
});
export type FleetCoAuthor = z.infer<typeof FleetCoAuthorSchema>;

/** GitHub's no-reply address for this account — the one GitHub attributes by id. */
export function coAuthorEmail(coAuthor: FleetCoAuthor): string {
  return `${coAuthor.github_user_id}+${coAuthor.login}@users.noreply.github.com`;
}

/** `Co-authored-by: <login> <id+login@users.noreply.github.com>`. */
export function formatCoAuthorTrailer(coAuthor: FleetCoAuthor): string {
  return `${CO_AUTHOR_TRAILER_KEY}: ${coAuthor.login} <${coAuthorEmail(coAuthor)}>`;
}

/** Any `Co-authored-by:` line — the shape a trailer paragraph is recognised by. */
export const CO_AUTHOR_LINE = /^[ \t]*co-authored-by[ \t]*:.*$/i;

/**
 * `message` with this co-author's trailer as its LAST line, exactly once.
 *
 * - `coAuthor` absent/null ⇒ `message` unchanged.
 * - `authorEmail` equal to the co-author's address ⇒ unchanged: a commit never
 *   credits its own author twice.
 * - A line already crediting the same address (a model that copied one) is removed
 *   first, so the trailer is written once and in the final paragraph — git and
 *   GitHub only read trailers from the message's last paragraph.
 *
 * `message` keeps its own trailing-newline convention; the trailer is appended to the
 * existing final paragraph when that paragraph is already a trailer block (so it
 * sits beside `Dz-Task-Id:`), else as a new paragraph. An existing
 * `Co-authored-by:` line for a DIFFERENT address is left alone: this credits one
 * more person, it never un-credits one.
 */
export function appendCoAuthorTrailer(
  message: string,
  coAuthor: FleetCoAuthor | null | undefined,
  authorEmail?: string,
): string {
  if (!coAuthor) return message;
  const email = coAuthorEmail(coAuthor).toLowerCase();
  if (authorEmail !== undefined && authorEmail.toLowerCase() === email) return message;
  const trailing = /\n*$/.exec(message)?.[0] ?? '';
  const kept = message
    .slice(0, message.length - trailing.length)
    .split('\n')
    .filter((line) => !(CO_AUTHOR_LINE.test(line) && line.toLowerCase().includes(`<${email}>`)))
    .join('\n')
    // A removed line must not leave an empty paragraph where it stood.
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s+$/, '');
  const trailer = formatCoAuthorTrailer(coAuthor);
  if (kept === '') return `${trailer}${trailing}`;
  // The FIRST paragraph is the subject, never a trailer block, even when it reads
  // `feat: …` — so a one-paragraph message always gets the trailer as a new paragraph.
  const cut = kept.lastIndexOf('\n\n');
  const joiner = cut !== -1 && isTrailerParagraph(kept.slice(cut + 2)) ? '\n' : '\n\n';
  return `${kept}${joiner}${trailer}${trailing}`;
}

/** `Key: value` on every line — git's own test for a trailer paragraph, simplified. */
function isTrailerParagraph(paragraph: string): boolean {
  const lines = paragraph.split('\n').filter((l) => l.trim() !== '');
  return lines.length > 0 && lines.every((l) => /^[A-Za-z0-9-]+[ \t]*:[ \t]*\S/.test(l));
}

/** One person the database found behind a task: their GitHub id (null ⇒ not linked) and login. */
export type CoAuthorCandidate = {
  readonly githubUserId: number | null;
  readonly login: string;
};

/**
 * The people behind a task, one per way a person can set fleet work in motion —
 * read by `findTaskCoAuthor` (`@developerz/db`):
 *
 * - `dispatcher` — `tasks.dispatched_by_user_id`: the person dispatched THIS task
 *   (`POST /v1/tasks`, a dashboard action, a single-repo `plan_start`'s own anchor).
 * - `planInitiator` — `plans.initiated_by_user_id` over `plan_groups.task_id`: the
 *   person who started the plan a scout cut this task from. Every such task is
 *   `system`/`scout`-dispatched, so this is the only row that knows a person asked.
 * - `askFiler` — `raw_tasks.filed_by_user_id` over `tasks.raw_task_id`: the person
 *   who filed the ask triage cut this task from (`system`/`raw_task_triage`).
 */
export type TaskCoAuthorCandidates = {
  readonly dispatcher: CoAuthorCandidate | null;
  readonly planInitiator: CoAuthorCandidate | null;
  readonly askFiler: CoAuthorCandidate | null;
};

/**
 * WHO a task's commits credit — the FIRST person on the ladder
 * dispatcher → plan initiator → ask filer, or `null`.
 *
 * The first person present DECIDES, and does not fall through: a person who acted
 * but has no linked GitHub (an SSO-only user) credits nobody, never whoever is on the
 * next rung — an explicit act beats an inferred one, the rule `taskOwnershipClaim`
 * states for the same ladder shape. A login GitHub would not accept (a `[bot]`, a
 * malformed row) credits nobody too, and never throws: this runs inside the claim
 * transaction, where a throw would roll the claim back over a cosmetic trailer.
 */
export function pickTaskCoAuthor(candidates: TaskCoAuthorCandidates): FleetCoAuthor | null {
  const person = candidates.dispatcher ?? candidates.planInitiator ?? candidates.askFiler;
  if (person === null || person.githubUserId === null) return null;
  const parsed = FleetCoAuthorSchema.safeParse({
    github_user_id: person.githubUserId,
    login: person.login,
  });
  return parsed.success ? parsed.data : null;
}

/**
 * `text` split before a trailing paragraph made only of `Co-authored-by:` lines, or
 * `null` when it does not end in one. For a writer that appends to a body whose
 * trailer must stay LAST (`spliceStackBlock`): a PR_BODY squash makes the body the
 * merged commit's message, and GitHub reads co-author trailers only from the final
 * paragraph. The first paragraph is never a trailer block.
 */
export function splitCoAuthorTail(text: string): { head: string; tail: string } | null {
  const cut = text.lastIndexOf('\n\n');
  if (cut === -1) return null;
  const tail = text.slice(cut + 2);
  const lines = tail.split('\n');
  if (tail.trim() === '' || !lines.every((line) => CO_AUTHOR_LINE.test(line))) return null;
  return { head: text.slice(0, cut), tail };
}
